import React, { useRef, useState, useMemo, useReducer } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  PanResponder,
  GestureResponderEvent,
  Platform,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { captureRef } from 'react-native-view-shot';

import { Button, GlobalErrorModal } from '../../../../shared/components';
import { buttonSizes } from '../../../../shared/constants/global';
import { signatureModalStyles } from '../../../../shared/components/modals/SignatureModal/SignatureModal.styles';
import { SignatureModalProps } from '../../../../shared/components/modals/SignatureModal/SignatureModalProps';
import { validate } from '../../../../shared/components/modals/SignatureModal/SignatureModalController';
import Colors from '../../../../shared/theme/colors';
import { useTranslation } from 'react-i18next';

type Point = { x: number; y: number };

const SignatureModal = ({
  visible,
  setVisible,
  setSignature,
}: SignatureModalProps) => {
  const [valid, setValid] = useState(false);
  const [msg, setMsg] = useState('');
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const { t } = useTranslation();

  /** Ref used to capture the SVG view */
  const svgContainerRef = useRef<View>(null);

  /** Stores completed strokes */
  const strokesRef = useRef<Point[][]>([]);
  /** Current stroke points */
  const currentRef = useRef<Point[]>([]);
  /** SVG path string */
  const pathRef = useRef<string>('');

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,

        onPanResponderGrant: (e: GestureResponderEvent) => {
          const { locationX: x, locationY: y } = e.nativeEvent;

          currentRef.current = [{ x, y }];
          pathRef.current += `M ${x} ${y} `;
          forceUpdate();
        },

        onPanResponderMove: (e: GestureResponderEvent) => {
          const { locationX: x, locationY: y } = e.nativeEvent;

          currentRef.current.push({ x, y });
          pathRef.current += `L ${x} ${y} `;
          forceUpdate();
        },

        onPanResponderRelease: () => {
          strokesRef.current.push(currentRef.current);
          currentRef.current = [];
          validate(strokesRef.current, setValid, setMsg, t);
        },
      }),
    [t],
  );

  const clear = () => {
    strokesRef.current = [];
    currentRef.current = [];
    pathRef.current = '';

    setValid(false);
    setMsg('');
    forceUpdate();
  };

  const saveSignature = async () => {
    if (!svgContainerRef.current) return;

    const base64 = await captureRef(svgContainerRef, {
      format: 'png',
      quality: 1,
      result: 'base64',
    });

    setSignature(`data:image/png;base64,${base64}`);
    setVisible(false);
    pathRef.current = ''
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={signatureModalStyles.overlay}>
        <View style={signatureModalStyles.modalContainer}>
          <TouchableOpacity
            style={signatureModalStyles.close}
            onPress={() => setVisible(false)}
          >
            <Text style={signatureModalStyles.closeText}>✕</Text>
          </TouchableOpacity>

          <Text style={signatureModalStyles.label}>
            {t('signatureScreen.title')}
          </Text>

          <View
            style={signatureModalStyles.canvas}
            {...panResponder.panHandlers}
            ref={svgContainerRef}
          >
            <Svg style={signatureModalStyles.canvasContainer}>
              <Path
                d={pathRef.current}
                stroke={Colors.black}
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>

          <View style={signatureModalStyles.actions}>
            <Button
              title={t('signatureScreen.clearCTA')}
              onPress={clear}
              fullWidth={false}
              size={buttonSizes.small}
            />

            <Text
              style={
                valid
                  ? signatureModalStyles.msg
                  : signatureModalStyles.errorMsg
              }
            >
              {msg}
            </Text>

            <Button
              title={t('signatureScreen.saveCTA')}
              onPress={saveSignature}
              fullWidth={false}
              size={buttonSizes.small}
              containerStyle={[
                signatureModalStyles.btn,
                !valid && signatureModalStyles.disabled,
              ]}
              disabled={!valid}
            />
          </View>
          {Platform.OS !== 'android' && <GlobalErrorModal/>}
        </View>
      </View>
    </Modal>
  );
};

export default SignatureModal;
